import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { CheckCircle, Loader2, Lock, Mail } from "lucide-react";
import { COUNTRIES, validateIntlPhone, splitE164 } from "../../utils/countries";
import PhoneInputIntl from "../PhoneInputIntl";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../i18n";
import { apiErrorMessage } from "../../utils/apiError";

/**
 * RC-WEB-4 §7/§8/§21 — Cuenta. ONE canonical profile field set.
 * The optional phone exists for account/support only: it is never shown to a
 * scanner, never embedded in the QR and never used for call routing.
 */

const API = "/api";

export const AccountSection = () => {
  const { refreshUser } = useAuth();
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changing, setChanging] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [phoneIso, setPhoneIso] = useState("PE");
  const [profile, setProfile] = useState({ full_name: "", email: "", phone: "", country_code: "PE" });
  const [passwords, setPasswords] = useState({ current_password: "", new_password: "", confirm_password: "" });

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/profile`);
      const countryCode = res.data.country_code || "PE";
      const parsed = splitE164(res.data.phone || "", countryCode);
      setPhoneIso(parsed.iso);
      setProfile({
        full_name: res.data.full_name || "",
        email: res.data.email || "",
        phone: parsed.national,
        country_code: countryCode,
      });
    } catch (_e) {
      toast.error(t("app.acc.load_error"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleSave = async () => {
    let e164 = profile.phone;
    if (profile.phone) {
      const v = validateIntlPhone(phoneIso, profile.phone);
      if (!v.valid) { setPhoneError(v.message); return; }
      e164 = v.e164;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/profile`, { ...profile, phone: e164, whatsapp: e164 });
      toast.success(t("app.acc.saved"));
      if (refreshUser) refreshUser();
    } catch (e) {
      toast.error(apiErrorMessage(e, t("app.acc.save_error")));
    } finally {
      setSaving(false);
    }
  };

  const handlePassword = async () => {
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error(t("app.acc.pwd_mismatch"));
      return;
    }
    if (passwords.new_password.length < 6) {
      toast.error(t("app.acc.pwd_short"));
      return;
    }
    setChanging(true);
    try {
      await axios.put(`${API}/profile/password`, {
        current_password: passwords.current_password,
        new_password: passwords.new_password,
      });
      toast.success(t("app.acc.pwd_updated"));
      setPasswords({ current_password: "", new_password: "", confirm_password: "" });
    } catch (e) {
      toast.error(apiErrorMessage(e, t("app.acc.pwd_error")));
    } finally {
      setChanging(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#1B84FF]" /></div>
    );
  }

  const field = "h-12 rounded-xl border-slate-300 bg-white text-[#0B2545] placeholder:text-slate-500 focus:border-[#1B84FF]";

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="settings-account-card">
        <h2 className="mb-1 text-lg font-semibold text-[#0B2545]">{t("app.acc.title")}</h2>
        <p className="mb-5 text-sm text-slate-600">{t("app.acc.desc")}</p>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="full_name" className="text-slate-700">{t("app.acc.full_name")}</Label>
            <Input id="full_name" value={profile.full_name} className={field}
                   onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                   data-testid="profile-name-input" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-700">{t("app.acc.email")}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
              <Input id="email" type="email" value={profile.email} className={`${field} pl-10`}
                     onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                     data-testid="profile-email-input" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="country_code" className="text-slate-700">{t("app.acc.country")}</Label>
            <p className="text-xs text-slate-600">{t("app.acc.country_hint")}</p>
            <select
              id="country_code"
              value={profile.country_code}
              onChange={(e) => { setProfile({ ...profile, country_code: e.target.value }); setPhoneIso(e.target.value); setPhoneError(""); }}
              className="h-12 w-full appearance-none rounded-xl border border-slate-300 bg-white px-3 text-sm text-[#0B2545] outline-none focus:border-[#1B84FF]"
              data-testid="profile-country-select"
            >
              {COUNTRIES.map((c) => (
                <option key={c.iso} value={c.iso}>{c.flag} {c.name}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="text-slate-700">{t("app.acc.phone")}</Label>
            <p className="text-xs text-slate-600" data-testid="profile-phone-optional-note">
              {t("app.acc.phone_note")}
            </p>
            <PhoneInputIntl
              iso={phoneIso}
              onIsoChange={(iso) => { setPhoneIso(iso); setPhoneError(""); }}
              value={profile.phone}
              onChange={(e) => { setProfile({ ...profile, phone: e.target.value }); setPhoneError(""); }}
              onBlur={() => {
                if (!profile.phone) return;
                const v = validateIntlPhone(phoneIso, profile.phone);
                if (!v.valid) setPhoneError(v.message);
                else { setPhoneError(""); setProfile({ ...profile, phone: v.national }); }
              }}
              error={phoneError}
              testId="profile-phone"
            />
          </div>

          <Button onClick={handleSave} disabled={saving}
                  className="h-12 w-full rounded-xl bg-[#1B84FF] text-white hover:bg-[#0F6FDB]"
                  data-testid="save-profile-btn">
            {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("app.acc.saving")}</>
                    : <><CheckCircle className="mr-2 h-4 w-4" /> {t("app.acc.save")}</>}
          </Button>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="settings-password-card">
        <h2 className="mb-1 text-lg font-semibold text-[#0B2545]">{t("app.acc.pwd_title")}</h2>
        <p className="mb-5 text-sm text-slate-600">{t("app.acc.pwd_desc")}</p>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current_password" className="text-slate-700">{t("app.acc.pwd_current")}</Label>
            <Input id="current_password" type="password" value={passwords.current_password} className={field}
                   onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })}
                   data-testid="current-password-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new_password" className="text-slate-700">{t("app.acc.pwd_new")}</Label>
            <Input id="new_password" type="password" value={passwords.new_password} className={field}
                   onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })}
                   data-testid="new-password-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm_password" className="text-slate-700">{t("app.acc.pwd_confirm")}</Label>
            <Input id="confirm_password" type="password" value={passwords.confirm_password} className={field}
                   onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })}
                   data-testid="confirm-password-input" />
          </div>
          <Button onClick={handlePassword} variant="outline"
                  disabled={changing || !passwords.current_password || !passwords.new_password}
                  className="h-12 w-full rounded-xl border-slate-300 text-[#0B2545]"
                  data-testid="change-password-btn">
            {changing ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("app.acc.saving")}</>
                      : <><Lock className="mr-2 h-4 w-4" /> {t("app.acc.pwd_btn")}</>}
          </Button>
        </div>
      </section>
    </div>
  );
};

export default AccountSection;
